import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axiosInstance from '../axiosConfig';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ru } from 'date-fns/locale';

function ReportDetails({ onCreateTemplate, onEditTemplate }) {
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [reportData, setReportData] = useState([]);
    const [expandedRows, setExpandedRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    useEffect(() => {
        axiosInstance.get('/api/get_report_templates/')
            .then(response => setTemplates(response.data.templates || []))
            .catch(error => console.error('Ошибка при загрузке шаблонов:', error));
    }, []);

    const handleGenerateReport = () => {
        setLoading(true);
        axiosInstance.get('/api/report/', {
            params: {
                start_date: moment(startDate).format('YYYY-MM-DD'),
                end_date: moment(endDate).format('YYYY-MM-DD'),
                template_id: selectedTemplate?.id,
            },
        })
            .then((response) => {
                setReportData(response.data.report_data || []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Ошибка при генерации отчета:', error);
                setLoading(false);
            });
    };

    const handleExportReport = (startDate, endDate, templateId) => {
        setDownloading(true);
        axiosInstance.get('/api/export_report_to_excel/', {
            params: {
                start_date: startDate,
                end_date: endDate,
                template_id: templateId,
            },
            responseType: 'blob',
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `report_${startDate}_to_${endDate}.xlsx`);
                document.body.appendChild(link);
                link.click();
                setDownloading(false);
            })
            .catch((error) => {
                console.error('Ошибка при выгрузке отчета:', error);
                setDownloading(false);
            });
    };

    const handleExportAllComponents = () => {
        setDownloading(true);
        axiosInstance.get('/api/export_all_components/', {
            responseType: 'blob',
        })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `all_components.xlsx`);
                document.body.appendChild(link);
                link.click();
                setDownloading(false);
            })
            .catch((error) => {
                console.error('Ошибка при выгрузке всех остатков:', error);
                setDownloading(false);
            });
    };

    const toggleRowDetails = (componentId) => {
        setExpandedRows(prevExpandedRows => ({
            ...prevExpandedRows,
            [componentId]: !prevExpandedRows[componentId]
        }));
    };

    const renderReportRows = () => {
        if (reportData.length === 0) {
            return (
                <tr>
                    <td colSpan={4}>Нет данных для отображения</td>
                </tr>
            );
        }

        return Object.entries(reportData).map(([componentId, data]) => (
            <React.Fragment key={componentId}>
                <tr 
                    className={`summary-row ${expandedRows[componentId] ? 'expanded' : ''}`}
                    onClick={() => toggleRowDetails(componentId)}
                    style={{ cursor: 'pointer' }}
                >
                    <td>{data.name}</td>
                    <td>{data.total_out}</td>
                    <td>{data.total_return}</td>
                    <td>{data.total_in}</td>
                </tr>
                {expandedRows[componentId] && (
                    <tr className="details-row">
                        <td colSpan={4}>
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Тип действия</th>
                                        <th>Пользователь</th>
                                        <th>Дата</th>
                                        <th>Количество</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.logs.map((log, index) => (
                                        <tr key={index}>
                                            <td>{log.action_type}</td>
                                            <td>{log.user}</td>
                                            <td>{log.date}</td>
                                            <td>{log.quantity}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        ));
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            display: 'flex',
            borderRadius: '4px 0 0 4px',
        }),
        container: (provided) => ({
            ...provided,
            display: 'flex',
            marginRight: '8px' // Отступ между селектом и календарем
        }),
    };

    const templateOptions = templates.map(template => ({
        value: template.id,
        label: (
            <div className="d-flex align-items-center justify-content-between">
                {template.name}
                <i 
                    className="fas fa-pencil-alt ms-2"
                    onClick={() => onEditTemplate(template.id)}
                    style={{ cursor: 'pointer' }}
                ></i>
            </div>
        ),
    }));

    return (
        <div className="container mt-4 simpl">
            <h4>Отчет по компонентам</h4>
            <div className="d-flex align-items-center mb-4 ">
                <div className="me-3">
                    <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(update) => setDateRange(update)}
                        isClearable={true}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Выберите период"
                        className="form-control"
                        locale={ru}
                    />
                </div>
                <div className="d-flex align-items-center me-3">
                    <Select
                        options={templateOptions}
                        onChange={(selectedOption) => {
                            const selected = templates.find(t => t.id === selectedOption.value);
                            setSelectedTemplate(selected);
                        }}
                        styles={customSelectStyles}
                        placeholder="Выберите шаблон"
                    />
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip-create-template">Добавить шаблон</Tooltip>}
                    >
                        <button 
                            className="btn btn-primary shab"
                            onClick={onCreateTemplate}
                            style={{ borderRadius: '0 4px 4px 0', padding: '0 10px' }}
                        >
                            <i className="fas fa-plus"></i>
                        </button>
                    </OverlayTrigger>
                </div>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-generate">Сгенерировать отчет</Tooltip>}
                >
                    <button 
                        className="btn btn-info ms-2" 
                        onClick={handleGenerateReport} 
                        disabled={loading || !startDate || !endDate}
                    >
                        {loading ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fas fa-file-alt"></i>
                        )}
                    </button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-export">Выгрузить в Excel</Tooltip>}
                >
                    <button 
                        className="btn btn-success ms-2" 
                        onClick={() => handleExportReport(moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), selectedTemplate?.id)} 
                        disabled={downloading || loading || reportData.length === 0}
                    >
                        {downloading ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fas fa-file-excel"></i>
                        )}
                    </button>
                </OverlayTrigger>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-export-all">Выгрузить все остатки</Tooltip>}
                >
                    <button 
                        className="btn btn-warning ms-2" 
                        onClick={handleExportAllComponents}
                        disabled={downloading}
                    >
                        {downloading ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fas fa-archive"></i>
                        )}
                    </button>
                </OverlayTrigger>
            </div>
            <div id="report-content">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '100px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Загрузка...</span>
                        </div>
                    </div>
                ) : (
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Компонент</th>
                                <th>Всего выдано</th>
                                <th>Всего возвращено</th>
                                <th>Всего поступило</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderReportRows()}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default ReportDetails;
